import type { MyProfileWorkspace } from '@onestep/camel-api';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useEffect } from 'react';
import { Cookies } from 'react-cookie';
import { useNavigate } from 'react-router';

import { DEFAULT_TIMEOUT } from '../../config/query-client';
import type { IClinicFeatureMap, IClinicPreview, IWebClientConfig } from '../../Pages/store';
import { getDisplayName } from '../../utils/nameUtil';
import useLoggedUser from '../useLoggedUser';
import useStores from '../useStores';

const cookies = new Cookies();

export enum clinicAllowedActions {
  allPatients = 'view_all_patients',
  pastData = 'read_past_data',
  modifyRole = 'modify_role',
  contentEditor = 'content_editor',
  readMedical = 'read_medical',
  viewDashboard = 'view_dashboard',
  read = 'read',
  write = 'write',
  viewAuditorTool = 'view_auditor_tool',
}

/**
 * @deprecated
 */
type memberRole = 'member' | 'clinician' | 'admin';

export interface IClinicMember {
  uuid: string;
  fullName: string;
  email: string;
  /**
   * @deprecated
   */
  role: memberRole;
  profileImageUrl?: string;
  discipline: string;
  username?: string;
  allowedActions?: string[];
}

export interface IClinic extends IClinicPreview {
  featureMap: IClinicFeatureMap;
  name: string;
  members: IClinicMember[];
  webClientConfig?: IWebClientConfig;
  allowedActions: string[];
  adminAccess?: boolean;
  org: string;
  orgDisplayName: string;
  profileImage?: string;
  locale?: string;
  urls?: Record<string, string>;
}

export interface IUseCurrentClinic {
  clinic: IClinic | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  hasWorkspaceLoadFailed: boolean;
  error: unknown;
  all: MyProfileWorkspace[];
  clinicians: IClinicMember[];

  getMember(uuid: string): IClinicMember | null;

  getMemberOrMyself(uuid: string): IClinicMember | null;

  update(data: Partial<IClinic>): Promise<void>;

  isKinomatic: boolean;

  setSelectedClinic(clinicName: string): void;

  isFox: boolean;
}

export default function useCurrentClinic(): IUseCurrentClinic {
  const { myProfile, organization, refetchProfile } = useLoggedUser();
  const rootStore = useStores();
  const { appStore, rtmStore } = rootStore;
  const navigate = useNavigate();
  const token = cookies.get('auth_timeout');

  const result = useQuery<IClinic>({
    queryKey: ['camel', 'workspace', appStore?.clinicUuid],
    enabled: !!appStore?.clinicUuid && !!token,
    staleTime: Infinity,
    retry: 1,
    cacheTime: 0,
    keepPreviousData: false,
    meta: {
      timeout: DEFAULT_TIMEOUT * 2,
    },
  });

  const mutate = useMutation({
    mutationFn: async (data: Partial<IClinic>) => {
      await axios.patch('/api/clinic/', data);
      await result.refetch();
    },
  });

  const setSelectedClinic = (clinicName: string) => {
    const clinicUuid = myProfile?.workspaces?.find(
      (clinic) => clinic.displayName === clinicName,
    )?.uuid;
    if (clinicUuid) {
      appStore.setClinic(clinicUuid);
    } else {
      navigate('/patients');
    }
  };

  const clinic = result.data;
  const isKinomatic = clinic?.org === 'kinomatic';

  useEffect(() => {
    if (clinic) {
      if (appStore.clinicOrg !== clinic.org) {
        appStore.clinicOrg = clinic.org;
        refetchProfile();
      }
      localStorage.setItem(
        'isShowMaxPrecision',
        clinic.webClientConfig?.walkPrecisionSettings?.maxPrecisionForWalks ? 'true' : 'false',
      );
    }
  }, [clinic?.uuid, clinic?.webClientConfig?.walkPrecisionSettings?.maxPrecisionForWalks]);

  useEffect(() => {
    if (rtmStore) {
      rtmStore.isSameOrganization = (!!clinic?.org && organization?.name === clinic?.org) ?? false;
    }
  }, [clinic?.org, organization?.name, rtmStore]);

  return {
    clinic,
    all: myProfile?.workspaces ?? [],
    clinicians: clinic?.members ?? [],
    getMember(uuid: string) {
      return clinic?.members?.find((x) => x.uuid === uuid) ?? null;
    },
    getMemberOrMyself(uuid: string) {
      if (uuid === myProfile?.uuid) {
        return {
          uuid: myProfile.uuid,
          email: myProfile.email,
          fullName: getDisplayName(myProfile),
        } as IClinicMember;
      }
      return clinic?.members?.find((x) => x.uuid === uuid) ?? null;
    },
    update: mutate.mutateAsync,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    isError: result.isError,
    hasWorkspaceLoadFailed: result.isLoadingError,
    error: result.error,
    isKinomatic,
    setSelectedClinic,
    isFox: clinic?.name.includes('fox-rehab') ?? false,
  };
}
